import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError, onSessionExpired, tokenStore } from '@/api'
import { AuthContext, type AuthStatus, type AuthValue } from './context'
import {
  currentUserSchema,
  fetchProfile,
  login as apiLogin,
  logout as apiLogout,
  type CurrentUser,
} from './authApi'

/**
 * Online identity (JWT). The last /auth/me result is cached so a phone that restarts without
 * network still knows who set it up; the PIN layer (auth/pin) decides who is *working*.
 */
const USER_KEY = 'ucsc.auth.user.v1'

function readCachedUser(): CurrentUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY)
    if (!raw) return null
    const parsed = currentUserSchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

function writeCachedUser(u: CurrentUser | null): void {
  try {
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u))
    else localStorage.removeItem(USER_KEY)
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<CurrentUser | null>(() =>
    tokenStore.get() ? readCachedUser() : null,
  )
  const [status, setStatus] = useState<AuthStatus>(() =>
    tokenStore.get() ? (readCachedUser() ? 'authenticated' : 'loading') : 'anonymous',
  )
  const [expired, setExpired] = useState(false)

  const applyUser = useCallback((u: CurrentUser | null) => {
    writeCachedUser(u)
    setUser(u)
    setStatus(u ? 'authenticated' : 'anonymous')
  }, [])

  const reload = useCallback(async () => {
    if (!tokenStore.get()) return applyUser(null)
    try {
      applyUser(await fetchProfile())
    } catch (e) {
      // Offline: keep the cached identity. Rejected: sign out.
      if (e instanceof ApiError && (e.kind === 'unauthorized' || e.kind === 'forbidden'))
        applyUser(null)
      else if (!readCachedUser()) setStatus('anonymous')
    }
  }, [applyUser])

  // Refresh the profile once on start. Offline keeps the cached identity.
  useEffect(() => {
    if (!tokenStore.get()) return
    let cancelled = false
    fetchProfile()
      .then((me) => !cancelled && applyUser(me))
      .catch((e: unknown) => {
        if (cancelled) return
        if (e instanceof ApiError && (e.kind === 'unauthorized' || e.kind === 'forbidden'))
          applyUser(null)
        else if (!readCachedUser()) setStatus('anonymous')
      })
    return () => {
      cancelled = true
    }
  }, [applyUser])

  useEffect(() => {
    const offExpired = onSessionExpired(() => {
      setExpired(true)
      applyUser(null)
      queryClient.clear()
    })
    const offTokens = tokenStore.subscribe((t) => {
      if (!t) applyUser(null)
    })
    return () => {
      offExpired()
      offTokens()
    }
  }, [applyUser, queryClient])

  const login = useCallback(
    async (identifier: string, password: string) => {
      await apiLogin(identifier, password)
      const me = await fetchProfile()
      setExpired(false)
      applyUser(me)
      return me
    },
    [applyUser],
  )

  const logout = useCallback(async () => {
    await apiLogout()
    applyUser(null)
    queryClient.clear()
  }, [applyUser, queryClient])

  const value = useMemo<AuthValue>(
    () => ({
      status,
      user,
      roles: user?.roles.map((r) => r.name) ?? [],
      login,
      logout,
      reload,
      expired,
    }),
    [status, user, login, logout, reload, expired],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
